import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { STATES } from "../../../util/constant";
import ConfirmationModal from "../../Common/ConfirmationModal";
import API from "../../../util/api";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";
const breakpoints = {
  tablet: 768,
  phone:  480,
};
const Container = styled.div`
  text-align: left;
  font-family: "Source Sans Pro", sans-serif;
  font-size: 13px;
  margin-top: 30px;
  min-height: 400px;
  background: #f8f8f8;
  border-radius: 5px;
  box-shadow: 2px 5px 5px rgba(0, 0, 0, 0.05);
  padding: 1.5em;
  @media (max-width: ${breakpoints.phone}px) {
      width: 450px;
      margin: 0 auto;
    }
`;

const SectionHeader = styled.h2`
  margin-top: 1em;
  margin-bottom: 0.5em;
  color: ${({ colors }) => colors.labelText};
`;

const Form = styled.form`
  width: 100%;
  button {
    margin-right: 1em;
    padding: 8px 16px;
    background: ${({ colors }) => colors.buttonBackground};
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    &:hover {
      background: ${({ colors }) => colors.buttonHoverBackground};
    }
  }

  @media (max-width: ${breakpoints.phone}px) {
    width: 400px;
  }
`;

const Table = styled.table`
  ${adminTable};
  border: 2px solid #666;
  margin-bottom: 1em;

  thead {
    display: none;
  }

  tbody td {
    ${adminTableDataCell};
    padding: 0.5em;
    vertical-align: top;
    border: 1px solid #666;
  }

  input[type="text"],
  select,
  textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 6px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 4px;
    font-size: 13px;
    font-family: inherit;
  }

  .noBorder {
    border: none;
  }

  @media (max-width: ${breakpoints.phone}px) {
    width: 380px;
  }
`;

const RadioLabel = styled.label`
  display: inline-block;
  margin-right: 1em;
  font-size: 13px;
  color: ${({ colors }) => colors.labelText};

  input {
    margin-right: 4px;
    vertical-align: middle;
  }
`;

const SubmitRow = styled.div`
  margin-top: 1em;
  button {
    margin-right: 1em;
    padding: 8px 16px;
    background: ${({ colors }) => colors.buttonBackground};
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    &:hover {
      background: ${({ colors }) => colors.buttonHoverBackground};
    }
    
  @media (max-width: ${breakpoints.phone}px) {
    flex-direction: column;
    gap: 10px;
  }
  }
`;
export default function SubmitQuoteContent({ colors }) {
  // --- FORM STATE ---
  const [searchType, setSearchType] = useState("quote_id");
  const [editQuote, setEditQuote] = useState("select");
  const [searchCode, setSearchCode] = useState("");

  const [orgName, setOrgName] = useState("");
  const [orgType, setOrgType] = useState("school");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [stateCode, setStateCode] = useState("select");
  const [zip, setZip] = useState("");
  const [schools, setSchools] = useState("");
  const [students, setStudents] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  const [product, setProduct] = useState("select");
  const [educators, setEducators] = useState("");
  const [discountPct, setDiscountPct] = useState("");
  const [discountAmt, setDiscountAmt] = useState("");
  const [amount, setAmount] = useState("0.00");
  const [total, setTotal] = useState("0.00");
  const [comment, setComment] = useState("");

  // Hidden fields
  const [numOfSchools, setNumOfSchools] = useState(0);
  const [quantity, setQuantity] = useState(0);

  // --- PRICES ---
  const prices = {
    erate: 600,
    gold: 1000,
    aup: 1500,
    ps: 1200,
    vpc: 1000,
    "erate-aup": 2100,
  };

  // recalc pricing
  useEffect(() => {
    const nSchools = orgType === "school" ? 1 : Number(schools || 0);
    const qty = product === "pdp" ? Number(educators || 0) : nSchools;
    setNumOfSchools(nSchools);
    setQuantity(qty);

    const basePrice = prices[product] || 0;
    const p = nSchools * basePrice;
    const pct = Number(discountPct) / 100;
    const da = discountPct ? p * pct : Number(discountAmt || 0);

    setAmount(p.toFixed(2));
    setDiscountAmt(discountPct ? (p * pct).toFixed(2) : discountAmt);
    setDiscountPct(
      !discountPct && discountAmt ? ((da / p) * 100).toFixed(2) : discountPct
    );
    setTotal((p - da).toFixed(2));
  }, [orgType, schools, product, educators, discountPct, discountAmt]);

  const showDistrictCount = orgType !== "school";
  const showEducators = product === "pdp";

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const [resultStatus, setResultStatus] = useState("Success");

  const resultFailed = resultStatus === "Failed";

  // payload builder
  const buildPayload = () => ({
    product_type:
      product === "erate"
        ? "DC4 Subscription"
        : product === "vpc"
        ? "ISAFE Direct Subscription"
        : product === "pdp"
        ? "PDP"
        : "",
    org_name: orgName,
    org_type: orgType.charAt(0).toUpperCase() + orgType.slice(1),
    street,
    city,
    state: stateCode,
    zip,
    schools: Number(schools || numOfSchools),
    students: Number(students || 0),
    first_name: firstName,
    last_name: lastName,
    contact_email: contactEmail,
    contact_phone: contactPhone,
    amount: Number(amount),
    discount_percentage: discountPct || "0",
    discount_amount: Number(discountAmt || 0),
    comments: comment || "",
  });

  const handleConfirm = async () => {
    setConfirmOpen(false);
    try {
      const resp = await API.requestInternalQuote(buildPayload());
      const status = resp?.status === "Failed" ? "Failed" : "Success";
      setResultMessage(resp.message || (status === "Failed" ? "Submission failed." : "Operation complete."));
      setResultStatus(status);
      setResultOpen(true);
    } catch (err) {
      setResultMessage(err.message || "Submission failed.");
      setResultStatus("Failed");
      setResultOpen(true);
    }
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setResultStatus("Success");
    if (!resultFailed) {
      // clear all fields
      setOrgName("");
      setOrgType("school");
      setStreet("");
      setCity("");
      setStateCode("select");
      setZip("");
      setSchools("");
      setStudents("");
      setFirstName("");
      setLastName("");
      setContactEmail("");
      setContactPhone("");
      setProduct("select");
      setEducators("");
      setDiscountPct("");
      setDiscountAmt("");
      setAmount("0.00");
      setTotal("0.00");
      setComment("");
    }
  };

  return (
    <Container colors={colors}>
      <h1 style={{ color: colors.labelText }}>
        Subscription Package Quotation
      </h1>
      <p>Please provide information below.</p>

      <Form colors={colors} onSubmit={(e) => e.preventDefault()}>
        {/* hidden fields */}
        <input type="hidden" name="data[num_of_schools]" value={numOfSchools} />
        <input type="hidden" name="data[quantity]" value={quantity} />
        <input type="hidden" name="data[customer_id]" value="" />
        <input type="hidden" name="data[promo_code]" value="" />

        {/* === Customer Information === */}
        <SectionHeader colors={colors}>Customer Information</SectionHeader>
        <Table colors={colors}>
          <tbody>
            {/* Row 1: Store Quote ID + dropdown */}
            <tr>
              <td width="30%" className="noBorder">
                <RadioLabel colors={colors}>
                  <input
                    type="radio"
                    name="data[search_type]"
                    id="SearchTypeQuoteId"
                    value="quote_id"
                    checked={searchType === "quote_id"}
                    onChange={() => {
                      setSearchType("quote_id");
                      setSearchCode("");
                    }}
                  />
                  <span>Store Quote ID</span>
                </RadioLabel>
              </td>
              <td className="noBorder">
                <select
                  id="editquote"
                  name="data[editquote]"
                  value={editQuote}
                  onChange={(e) => setEditQuote(e.target.value)}
                >
                  <option value="select">Select</option>
                  {/* …your quote options here… */}
                </select>
              </td>
              <td className="noBorder">&nbsp;</td>
            </tr>

            {/* Row 2: Customer Code & Invoice Number with search bar */}
            <tr>
              <td width="30%" className="noBorder">
                <RadioLabel colors={colors}>
                  <input
                    type="radio"
                    name="data[search_type]"
                    id="SearchTypeCustomerCode"
                    value="customer_code"
                    checked={searchType === "customer_code"}
                    onChange={() => {
                      setSearchType("customer_code");
                      setSearchCode("");
                    }}
                  />
                  <span>Customer Code</span>
                </RadioLabel>
                <RadioLabel colors={colors}>
                  <input
                    type="radio"
                    name="data[search_type]"
                    id="SearchTypeInvoiceNumber"
                    value="invoice_number"
                    checked={searchType === "invoice_number"}
                    onChange={() => {
                      setSearchType("invoice_number");
                      setSearchCode("");
                    }}
                  />
                  <span>Invoice Number</span>
                </RadioLabel>
              </td>
              <td className="noBorder">
                <input
                  type="text"
                  name="data[search_code]"
                  id="search_code"
                  placeholder="Enter code or invoice…"
                  value={searchCode}
                  onChange={(e) => setSearchCode(e.target.value)}
                />
              </td>
              <td className="noBorder" style={{ textAlign: "right" }}>
                <button type="button">Search</button>
              </td>
            </tr>

            {/* spacer */}
            <tr>
              <td colSpan="3" className="noBorder">
                &nbsp;
              </td>
            </tr>

            {/* Name of Organization */}
            <tr>
              <td>Name of Organization*</td>
              <td colSpan="2">
                <input
                  type="text"
                  name="data[org_name]"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                />
              </td>
            </tr>

            {/* Type of Organization */}
            <tr>
              <td>Type of Organization*</td>
              <td colSpan="2">
                {["school", "district", "providers"].map((type) => (
                  <RadioLabel key={type} colors={colors}>
                    <input
                      type="radio"
                      name="data[org_type]"
                      value={type}
                      checked={orgType === type}
                      onChange={(e) => setOrgType(type)}
                    />
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </RadioLabel>
                ))}
              </td>
            </tr>

            {/* Street */}
            <tr>
              <td>Street</td>
              <td colSpan="2">
                <input
                  type="text"
                  name="data[street]"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                />
              </td>
            </tr>

            {/* City */}
            <tr>
              <td>City</td>
              <td colSpan="2">
                <input
                  type="text"
                  name="data[city]"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </td>
            </tr>

            {/* State */}
            <tr>
              <td>State</td>
              <td colSpan="2">
                <select
                  name="data[state]"
                  value={stateCode}
                  onChange={(e) => setStateCode(e.target.value)}
                >
                  <option value="select">Select</option>
                  {Object.entries(STATES).map(([name, abbr]) => (
                    <option key={abbr} value={abbr}>
                      {name}
                    </option>
                  ))}
                </select>
              </td>
            </tr>

            {/* Zip */}
            <tr>
              <td>Zip</td>
              <td>
                <input
                  type="text"
                  name="data[zip]"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                />
              </td>
            </tr>

            {/* Number of Schools in District */}
            {showDistrictCount && (
              <tr>
                <td>Number of Schools in District*</td>
                <td>
                  <input
                    type="text"
                    name="data[schools]"
                    value={schools}
                    onChange={(e) => setSchools(e.target.value)}
                  />
                </td>
                <td>Schools</td>
              </tr>
            )}

            {/* Number of Students */}
            <tr>
              <td>Number of Students in the District</td>
              <td>
                <input
                  type="text"
                  name="data[students]"
                  value={students}
                  onChange={(e) => setStudents(e.target.value)}
                />
              </td>
            </tr>
          </tbody>
        </Table>

        {/* === Contact Information === */}
        <SectionHeader colors={colors}>Contact Information</SectionHeader>
        <Table colors={colors}>
          <tbody>
            <tr>
              <td>First Name*</td>
              <td>
                <input
                  type="text"
                  name="data[first_name]"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <td>Last Name*</td>
              <td>
                <input
                  type="text"
                  name="data[last_name]"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <td>E-mail*</td>
              <td>
                <input
                  type="text"
                  name="data[contact_email]"
                  placeholder="Please provide a valid email."
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <td>Phone</td>
              <td>
                <input
                  type="text"
                  name="data[contact_phone]"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
              </td>
            </tr>
          </tbody>
        </Table>

        <SectionHeader colors={colors}>Product Information</SectionHeader>
        <Table colors={colors}>
          <tbody>
            <tr>
              <td>Product type*</td>
              <td>
                <select
                  name="data[product]"
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  className="prd"
                >
                  <option value="select">Select</option>
                  <option value="erate">DC4 Subscription</option>
                  <option value="vpc">ISAFE Direct Subscription</option>
                  <option value="pdp">PDP (shows educators)</option>
                </select>
              </td>
            </tr>
            {showEducators && (
              <tr>
                <td>Number of Educators</td>
                <td>
                  <input
                    type="text"
                    name="data[educators]"
                    value={educators}
                    onChange={(e) => setEducators(e.target.value)}
                  />
                </td>
              </tr>
            )}
            <tr>
              <td>Amount</td>
              <td>${amount}</td>
            </tr>
            <tr>
              <td>Discount (%)</td>
              <td>
                <input
                  type="text"
                  name="data[discount]"
                  value={discountPct}
                  onChange={(e) => {
                    setDiscountPct(e.target.value);
                    setDiscountAmt("");
                  }}
                />
              </td>
            </tr>
            <tr>
              <td>Discount ($)</td>
              <td>
                <input
                  type="text"
                  name="data[discount_amount]"
                  value={discountAmt}
                  onChange={(e) => {
                    setDiscountAmt(e.target.value);
                    setDiscountPct("");
                  }}
                />
              </td>
            </tr>
            <tr>
              <td>Total</td>
              <td>${total}</td>
            </tr>
            <tr>
              <td>Comment:</td>
              <td>
                <textarea
                  name="data[comment]"
                  rows="4"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </td>
            </tr>
          </tbody>
        </Table>

        <SubmitRow colors={colors}>
          <button type="button" onClick={() => setConfirmOpen(true)}>
            Create Quote and Finish
          </button>
          <button type="button" onClick={() => setConfirmOpen(true)}>
            Create Quote and Next
          </button>
        </SubmitRow>
      </Form>
      <ConfirmationModal
        show={confirmOpen}
        title="Confirm Submission"
        message="Are you sure you want to submit this quote?"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
      />

      {/* show result */}
      <ConfirmationModal
        show={resultOpen}
        title={resultFailed ? "Error" : "Success"}
        message={resultMessage}
        onCancel={handleResultClose}
        onConfirm={handleResultClose}
        status={resultStatus}
        single
      />
    </Container>
  );
}
